import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TaskItem, TaskPriority } from '../../../models/TaskItem';
import ModalDialog from '../../common/ModalDialog/ModalDialog';
import TextInput from '../../widgets/TextInput';
import DateInput from '../../widgets/DateInput';
import SelectInput from '../../widgets/SelectInput';
import { FaSave, FaTimes, FaTrash, FaCheck, FaUndo } from 'react-icons/fa';
import './TaskManagement.scss';

interface TaskDialogProps {
    open: boolean;
    task: TaskItem | null;
    onSave: (task: TaskItem) => Promise<TaskItem>;
    onDelete: (task: TaskItem) => void;
    onComplete: (task: TaskItem) => void;
    onReopen: (task: TaskItem) => void;
    onClose: () => void;
}

const emptyTask = (): TaskItem => ({
    id: '',
    title: '',
    description: '',
    dueDate: null,
    isCompleted: false,
    priority: TaskPriority.Medium,
    assignedToUserId: null,
    createdAt: new Date().toISOString(),
    completedAt: null,
});

const normalizeDueDate = (task: TaskItem): TaskItem => {
    if (!task.dueDate) return task;
    const date = new Date(task.dueDate);
    if (isNaN(date.getTime())) return task;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return { ...task, dueDate: `${year}-${month}-${day}` };
};

const TaskDialog: React.FC<TaskDialogProps> = ({
    open, task, onSave, onDelete, onComplete, onReopen, onClose,
}) => {
    const { t } = useTranslation(['common', 'screens']);
    const [data, setData] = useState<TaskItem>(emptyTask());
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setData(task ? normalizeDueDate({ ...task }) : emptyTask());
        setShowDeleteConfirm(false);
    }, [task, open]);

    if (!open) return null;

    const isNew = !task?.id;
    const set = <K extends keyof TaskItem>(field: K, value: TaskItem[K]) =>
        setData(prev => ({ ...prev, [field]: value }));

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            const saved = await onSave(data);
            setData(normalizeDueDate({ ...saved }));
        } catch {
            // parent handles error display
        } finally {
            setSaving(false);
        }
    };

    const priorityOptions = [
        { label: t('screens:tasks.priority.0'), value: TaskPriority.Low },
        { label: t('screens:tasks.priority.1'), value: TaskPriority.Medium },
        { label: t('screens:tasks.priority.2'), value: TaskPriority.High },
    ];

    const formatDate = (iso: string | null) => {
        if (!iso) return '';
        const d = new Date(iso);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleDateString();
    };

    return (
        <ModalDialog
            open={open}
            title={isNew ? t('screens:tasks.dialog.newTitle') : t('screens:tasks.dialog.editTitle')}
            subtitle={!isNew ? task?.title : undefined}
            onClose={onClose}
            onSubmit={handleSave}
            width={480}
            minHeight={400}
            minWidth="40vw"
            footerLeft={!isNew ? (
                showDeleteConfirm ? (
                    <div className="task-dialog__delete-confirm">
                        <span className="task-dialog__delete-confirm-text">{t('common:areYouSure')}</span>
                        <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => onDelete(data)}
                        >
                            <FaTrash style={{ marginRight: 5 }} />
                            {t('common:yesDelete')}
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => setShowDeleteConfirm(false)}
                        >
                            {t('common:cancel')}
                        </button>
                    </div>
                ) : (
                    <div className="task-dialog__footer-left-actions">
                        <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => setShowDeleteConfirm(true)}
                        >
                            <FaTrash style={{ marginRight: 5 }} />
                            {t('common:delete')}
                        </button>
                        {data.isCompleted ? (
                            <button
                                type="button"
                                className="btn btn-outline-warning btn-sm"
                                onClick={() => onReopen(data)}
                            >
                                <FaUndo style={{ marginRight: 5 }} />
                                {t('screens:tasks.dialog.reopen')}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-outline-success btn-sm"
                                onClick={() => onComplete(data)}
                            >
                                <FaCheck style={{ marginRight: 5 }} />
                                {t('screens:tasks.dialog.complete')}
                            </button>
                        )}
                    </div>
                )
            ) : undefined}
            footerRight={
                <>
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                        <FaSave style={{ marginRight: 5 }} />
                        {saving ? t('common:saving') : t('common:save')}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={onClose}>
                        <FaTimes style={{ marginRight: 5 }} />
                        {t('common:close')}
                    </button>
                </>
            }
        >
            <div className="task-dialog__form">
                {/* Task Details section */}
                <div className="dialog-section">
                    <div className="dialog-section-title">
                        {t('screens:tasks.dialog.sections.taskDetails')}
                    </div>

                    <TextInput
                        label={t('screens:tasks.dialog.fields.title')}
                        value={data.title ?? ''}
                        required
                        onChange={v => set('title', v)}
                    />

                    <div className="dialog-row">
                        <div className="dialog-col">
                            <SelectInput
                                label={t('screens:tasks.dialog.fields.priority')}
                                value={data.priority}
                                options={priorityOptions}
                                onChange={v => set('priority', v as TaskPriority)}
                            />
                        </div>
                        <div className="dialog-col">
                            <DateInput
                                label={t('screens:tasks.dialog.fields.dueDate')}
                                value={data.dueDate ?? ''}
                                onChange={v => set('dueDate', v || null)}
                            />
                        </div>
                    </div>

                    <div className="mb-3">
                        <label className="form-label">
                            {t('screens:tasks.dialog.fields.description')}
                        </label>
                        <textarea
                            className="form-control"
                            rows={4}
                            value={data.description ?? ''}
                            onChange={e => set('description', e.target.value)}
                            placeholder={t('screens:tasks.dialog.fields.descriptionPlaceholder')}
                        />
                    </div>
                </div>

                {/* Task Info section (existing tasks only) */}
                {!isNew && (
                    <div className="dialog-section">
                        <div className="dialog-section-title">
                            {t('screens:tasks.dialog.sections.taskInfo')}
                        </div>
                        <div className="task-dialog__info-grid">
                            <div className="task-dialog__info-item">
                                <span className="task-dialog__info-label">
                                    {t('screens:tasks.dialog.fields.status')}
                                </span>
                                <span>
                                    {data.isCompleted
                                        ? <span className="badge bg-success">{t('screens:tasks.status.completed')}</span>
                                        : <span className="badge bg-primary">{t('screens:tasks.status.open')}</span>
                                    }
                                </span>
                            </div>
                            {data.createdAt && (
                                <div className="task-dialog__info-item">
                                    <span className="task-dialog__info-label">
                                        {t('screens:tasks.dialog.fields.createdAt')}
                                    </span>
                                    <span>{formatDate(data.createdAt)}</span>
                                </div>
                            )}
                            {data.completedAt && (
                                <div className="task-dialog__info-item">
                                    <span className="task-dialog__info-label">
                                        {t('screens:tasks.dialog.fields.completedAt')}
                                    </span>
                                    <span>{formatDate(data.completedAt)}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </ModalDialog>
    );
};

export default TaskDialog;


